<template>
  <div>
    <div class="flex items-center justify-between py-4 pb-2 px-2">
      <div class="flex items-center">
        <TentTree class="w-5 h-5 mx-2 text-gray-600" />
        <h1 class="text-gray-900">Program Progress</h1>
      </div>
      <div class="flex items-center gap-4">
        <!-- Program Status Dropdown -->
        <div class="relative">
          <select
            v-model="selectedProgramStatus"
            class="text-xs px-3 pr-8 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent relative appearance-none"
          >
            <option value="">All Program Status</option>
            <option
              v-for="(value, key) in programStatusChoices"
              :key="key"
              :value="key"
            >
              {{ value }}
            </option>
          </select>
          <ChevronDown class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
        </div>

        <!-- Country Dropdown -->
        <div class="relative">
          <select
            v-model="selectedCountry"
            class="text-xs px-3 pr-8 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent relative appearance-none"
            :disabled="countryStore.isLoading"
          >
            <option value="all">All Countries</option>
            <option
              v-for="country in countryStore.fellowCountries"
              :key="country.value"
              :value="country.value"
            >
              {{ country.label }}
            </option>
          </select>
          <ChevronDown class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
        </div>

        <!-- Batch Dropdown -->
        <div class="relative">
          <select
            v-model="selectedBatchId"
            class="text-xs px-3 pr-8 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent appearance-none"
          >
            <option value="">All Batches</option>
            <option v-for="batch in batchChoices" :key="batch.id" :value="batch.id">
              {{ batch.name }}
            </option>
          </select>
          <ChevronDown class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
        </div>

        <!-- Search Bar -->
        <div class="relative w-[300px]">
          <Search
            class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search adventurers..."
            class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            v-model="searchQuery"
            @input="debouncedSearch"
          />
        </div>
      </div>
    </div>

    <div class="py-4 px-2 pt-2">
      <div v-if="isBatchChoicesLoading" class="flex items-center justify-center py-8 min-h-[calc(100vh-4rem)]">
        <OuttalentLoader />
      </div>
      <div
        v-else-if="isLoadingAdventurers || isSearching"
        class="flex items-center justify-center py-8"
      >
        <div class="text-center">
          <Loader2 class="animate-spin h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p class="text-gray-500">
            {{ isSearching ? 'Searching adventurers...' : 'Loading adventurers...' }}
          </p>
        </div>
      </div>
      <div
        v-else-if="hasSearchQuery && searchResults.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <SearchX class="w-12 h-12 text-gray-400 mb-3" />
        <h3 class="text-lg font-medium text-gray-700 mb-1">No adventurers found</h3>
        <p class="text-gray-500 mb-4">No adventurers match your search "{{ searchQuery }}"</p>
        <button
          @click="clearSearch"
          class="text-sm text-cosmicPurple hover:text-cosmicPurple/80 flex items-center gap-1.5"
        >
          <RotateCcw class="w-4 h-4" />
          Load all adventurers
        </button>
      </div>
      <div v-else-if="searchResults.length > 0" class="flex flex-col gap-4">
        <div v-for="batch in searchResults" :key="batch.id">
          <h2 class="text-lg font-medium text-gray-700 mb-2 ml-2">{{ batch.name }}</h2>
          <BatchProgressTable
          :key="batch.id"
          :batch-name="batch.name"
          :adventurers="batch.adventurers"
          :clickable-header="true"
          @header-click="() => handleBatchSelect(batch.id)"
        />
        <hr class="border-gray-200 my-4" />
        </div>
        <!-- Search results -->
        
      </div>
      <div v-else-if="!selectedBatchId && batches.length > 0" class="flex flex-col gap-4">
        <!-- All batches view -->
         <div v-for="batch in batches" :key="batch.id">
          <h2 class="text-lg font-medium text-gray-700 ml-2 -mb-2">{{ batch.name }}</h2>
          <BatchProgressTable
            :key="batch.id"
            :batch-name="batch.name"
            :adventurers="batch.adventurers"
          />
          <hr class="border-gray-200 my-4" />
         </div>

      </div>
      <div v-else-if="selectedBatchId && selectedBatch?.results" class="flex flex-col gap-4">
        <!-- Single batch view -->
        <h2 class="text-lg font-medium text-gray-700 ml-2 -mb-2">{{ formatBatchName(selectedBatchId) }}</h2>
        <BatchProgressTable
          :batch-name="formatBatchName(selectedBatchId)"
          :adventurers="selectedBatch.results"
        />
      </div>
      <div v-else-if="selectedBatchId" class="text-center text-gray-500 mt-8">
        No adventurers found in this batch.
      </div>
      <div
        v-else-if="!hasSearchQuery"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <Search class="w-12 h-12 text-gray-400 mb-3" />
        <button
          @click="clearSearch"
          class="text-cosmicPurple hover:text-cosmicPurple/80 flex items-center gap-1.5"
        >
          <RotateCcw class="w-4 h-4" />
          Show all adventurers
        </button>
      </div>
      <div v-else class="text-center text-gray-500 mt-8">No adventurers found.</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Loader2, SearchX, RotateCcw, Search, TentTree, ChevronDown } from 'lucide-vue-next'
import BatchProgressTable from '@/components/manage/BatchProgressTable.vue'
import { useAdventurersStore } from '@/stores/adventurers'
import { useCountryStore } from '@/stores/countries'
import { useDebounce } from '@/composables/useDebounce'
import { programStatusChoices } from '@/const/fellowChoices'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'

const route = useRoute()
const router = useRouter()
const adventurersStore = useAdventurersStore()
const countryStore = useCountryStore()
const { debounce } = useDebounce(300) // 300ms delay

const batches = computed(() => adventurersStore.batches)
const selectedBatchId = ref<string | null>(null)
const selectedBatch = computed(() => {
  if (!selectedBatchId.value || !adventurersStore.selectedBatchData) return null
  return {
    results: adventurersStore.selectedBatchData,
    count: adventurersStore.selectedBatchData.length
  }
})

const searchQuery = ref('')
const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0)
const searchResults = computed(() => adventurersStore.searchResults)
const selectedCountry = ref<number | 'all'>('all')
const selectedProgramStatus = ref('')

const clearSearch = async () => {
  searchQuery.value = ''
  adventurersStore.clearSearch()
  adventurersStore.clearSelectedBatch()
  selectedBatchId.value = null
  await router.replace({
    query: Object.fromEntries(
      Object.entries(route.query).filter(([key]) => !['batch', 'q'].includes(key))
    )
  })
  await fetchAdventurers()
}

const handleSearch = async () => {
  if (!searchQuery.value.trim()) {
    adventurersStore.clearSearch()
    return
  }

  try {
    // Clear batch selection without triggering a fetch
    if (selectedBatchId.value) {
      selectedBatchId.value = null
      // Update URL without triggering the watch
      await router.replace({
        query: {
          ...route.query,
          batch: undefined,
          q: searchQuery.value.trim()
        }
      })
    }

    await adventurersStore.searchAdventurers(searchQuery.value.trim(), {
      country: route.query.country ? Number(route.query.country) : undefined,
      outtalent_program_status: route.query.programStatus as string || undefined
    })
  } catch (error) {
    console.error('Error searching fellows:', error)
  }
}

const debouncedSearch = debounce(handleSearch)

// Clear filters when selecting batch
const handleBatchSelect = async (batchId: string) => {
  searchQuery.value = ''
  adventurersStore.clearSearch()
  selectedBatchId.value = batchId
  selectedCountry.value = 'all' // Clear country when selecting batch
  selectedProgramStatus.value = '' // Clear program status when selecting batch
  await router.replace({
    query: {
      batch: batchId,
      blur: route.query.blur,
      q: undefined,
      country: undefined,
      programStatus: undefined
    }
  })
  await fetchAdventurers()
}

const batchChoices = computed(() => adventurersStore.batchChoices)
const isBatchChoicesLoading = computed(() => adventurersStore.isLoading)
const isLoadingAdventurers = computed(() => adventurersStore.isLoading)
const isSearching = computed(() => adventurersStore.isSearching)

const fetchAdventurers = async () => {
  // Don't fetch if we're in search mode and have search results
  if (hasSearchQuery.value && searchResults.value.length > 0) {
    return
  }

  // If there's a program status filter, use that
  if (route.query.programStatus) {
    await adventurersStore.fetchAdventurersByProgramStatus(route.query.programStatus as string)
    return
  }

  // If there's a country filter, use that instead of batch filtering
  if (route.query.country) {
    await adventurersStore.fetchAdventurersByCountry(Number(route.query.country))
    return
  }

  if (selectedBatchId.value) {
    await adventurersStore.fetchAdventurersByBatch(selectedBatchId.value, { exclude_paused: true })
  } else {
    await adventurersStore.fetchAllBatches({ exclude_paused: true })
  }
}

const formatBatchName = (batchId: string) => {
  return batchId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

onMounted(async () => {
  await Promise.all([adventurersStore.fetchBatchChoices(), countryStore.fetchFellowCountries()])

  // Handle initial URL query parameters
  const { batch, country, programStatus } = route.query

  if (country) {
    selectedCountry.value = parseInt(country as string) || 'all'
  }

  if (batch) {
    selectedBatchId.value = batch as string
  } else if (!country && batchChoices.value.length > 0) {
    // If no batch in query and no country, use the latest batch
    const batchToSelect = batchChoices.value[0].id
    selectedBatchId.value = batchToSelect
    await router.replace({
      query: { ...route.query, batch: batchToSelect }
    })
  }

  if (programStatus) {
    selectedProgramStatus.value = programStatus as string
  }

  // Fetch adventurers after setting parameters
  await fetchAdventurers()
})

// Update watch to use router.replace instead of full navigation
watch(selectedBatchId, async (newBatchId: string | null, oldBatchId: string | null) => {
  // Only handle batch changes that weren't triggered by search
  if (!hasSearchQuery.value) {
    if (newBatchId) {
      await router.replace({
        query: {
          batch: newBatchId,
          blur: route.query.blur,
          country: undefined,
          programStatus: undefined
        }
      })
    } else if (oldBatchId) {
      await router.replace({
        query: { blur: route.query.blur }
      })
    }
    await fetchAdventurers()
  }
})

// Update country watch to handle program status
watch(selectedCountry, async (newValue) => {
  // If selecting a country, clear other filters
  if (newValue !== 'all') {
    selectedBatchId.value = null
    selectedProgramStatus.value = '' // Clear program status when selecting country
  }

  // Update URL with only country parameter
  await router.replace({
    query: {
      country: newValue === 'all' ? undefined : newValue.toString(),
      batch: newValue !== 'all' ? undefined : route.query.batch,
      programStatus: newValue !== 'all' ? undefined : route.query.programStatus,
      blur: route.query.blur
    }
  })

  if (newValue === 'all') {
    adventurersStore.clearSearch()
    if (selectedBatchId.value) {
      await adventurersStore.fetchAdventurersByBatch(selectedBatchId.value)
    } else {
      await adventurersStore.fetchAllBatches()
    }
  } else {
    await adventurersStore.fetchAdventurersByCountry(newValue as number)
  }
})

// Watch for route query country changes (for back/forward navigation)
watch(
  () => route.query.country,
  async (newCountry) => {
    if (newCountry && parseInt(newCountry as string) !== selectedCountry.value) {
      selectedCountry.value = parseInt(newCountry as string) || 'all'
    } else if (!newCountry && selectedCountry.value !== 'all') {
      selectedCountry.value = 'all'
    }
  }
)

// Watch for program status changes
watch(selectedProgramStatus, async (newValue) => {
  // Update URL with program status parameter
  await router.replace({
    query: {
      ...route.query,
      programStatus: newValue || undefined,
      batch: newValue ? undefined : route.query.batch
    }
  })

  if (!newValue) {
    adventurersStore.clearSearch()
    if (selectedBatchId.value) {
      await adventurersStore.fetchAdventurersByBatch(selectedBatchId.value)
    } else {
      await adventurersStore.fetchAllBatches()
    }
  } else {
    await adventurersStore.fetchAdventurersByProgramStatus(newValue)
  }
})

// Watch for route query program status changes (for back/forward navigation)
watch(
  () => route.query.programStatus,
  async (newProgramStatus) => {
    if (newProgramStatus && newProgramStatus !== selectedProgramStatus.value) {
      selectedProgramStatus.value = newProgramStatus as string
    } else if (!newProgramStatus && selectedProgramStatus.value) {
      selectedProgramStatus.value = ''
    }
  }
)
</script>